/**
 * Attendance domain family.
 *
 * Shape on PROD (and every newly-seeded school):
 *
 *   attendance                  ← parent/orchestration definition, no UI surface
 *     ├── daily-register        ← governed child, register-enabled
 *     └── period-register       ← governed child, register-enabled
 *
 * Both children share the `attendanceEvents` collection and the same status
 * vocabulary. The `domainId` field on each event discriminates them so a
 * single query can ask "all attendance events for student X today" without
 * caring whether the mark came from the homeroom roll or a period sweep.
 *
 * The `period-register` child inherits today's `daily-register` marks via
 * `inheritsFrom` — the register engine pre-seeds card states from the daily
 * sweep and teachers can still override.
 *
 * Schema shape (replaces the old `source`-tagged fields array):
 *   primary  — the singular headline field (Status). One Quick Issue column.
 *   followUp — secondary user-entered inputs (reason / excuse / document)
 *              opened in the individual-entry form. Conditional reveal is
 *              decided by the form component, not the schema.
 *
 * Universal engine-filled fields (recordedBy, occurredAt, state) are NOT
 * declared here — the engine writes them implicitly. Register-engine context
 * (periodId, classId, arrivalTime, departureTime) likewise lives in the
 * register write path, not the config.
 *
 * `assertDomainShape` at the bottom throws synchronously on import if the
 * shape drifts — bogus configs can't run, can't build, can't test.
 */

import { assertDomainShape } from '../schemaAssert.js';
import { buildDomainCapabilities, buildDomainSurfaces } from '../centralModel.js';

export const ATTENDANCE_DOMAIN_ID = 'attendance';
export const DAILY_REGISTER_DOMAIN_ID = 'daily-register';
export const PERIOD_REGISTER_DOMAIN_ID = 'period-register';

const PRIMARY = {
  id: 'status',
  label: 'Status',
  type: 'multiChoice',
  options: [
    { value: 'present', label: 'Present', color: 'success' },
    { value: 'late',    label: 'Late',    color: 'warning' },
    { value: 'absent',  label: 'Absent',  color: 'error'   },
    { value: 'excused', label: 'Excused', color: 'info'    },
  ],
};

const FOLLOW_UP = [
  { id: 'reason', label: 'Reason', type: 'string', optional: true },
  {
    id: 'excuseType',
    label: 'Excuse',
    type: 'multiChoice',
    optional: true,
    options: [
      { value: 'medical', label: 'Medical' },
      { value: 'family',  label: 'Family'  },
      { value: 'other',   label: 'Other'   },
    ],
  },
  { id: 'documentRef', label: 'Document', type: 'storageRef', optional: true },
];

/**
 * Parent-loop rule — declarative form of the outbound trigger. It replaced the
 * hardcoded, immediate-send `streakRuleService` (now removed). When
 * `parentLoopTrigger` sees three
 * consecutive school-day absences for a student it PROPOSES a parent message
 * (writes a `status:'proposed'` action to `attendanceActions`); a teacher
 * reviews and sends it (QuickIssue, T2). The parent's reply writes back onto
 * the named absence events via the `confirm`/`dispute` transitions.
 *
 * This is data, not code: any domain gets the same loop by declaring its own
 * `parentLoop`. `condition.type` is interpreted by `parentLoopTrigger`
 * (`consecutiveStatus` = N-in-a-row of `value`, calendar-aware).
 */
const PARENT_LOOP = {
  ruleId: 'attendance.absent_3_consecutive',
  title: 'Absence check',
  prompt: 'Our records show three consecutive days marked absent. Was this absence known to you?',
  decisionLabel: 'Please confirm',
  condition: {
    type: 'consecutiveStatus',
    statusFieldId: 'status',
    value: 'absent',
    count: 3,
    calendarAware: true,
  },
  // Parent taps "Yes, I knew" → excuse the marked days (excusedVia:'parent-response').
  confirm: {
    optionId: 'confirm',
    label: 'Yes, I knew about this',
    fromStatus: 'absent',
    status: 'excused',
    set: { excusedVia: 'parent-response' },
  },
  // Parent taps "No" → flag the events for staff follow-up, don't change status.
  dispute: {
    optionId: 'dispute',
    label: "No, this isn't right",
    flag: 'parentDisputed',
  },
};

const SHARED_TERMINOLOGY = {
  event: { label: 'attendance entry', plural: 'attendance entries', description: 'A presence record for a student' },
  category: { label: 'status', plural: 'statuses', description: 'Present, late, absent, or excused' },
  type: { label: 'attendance type', plural: 'attendance types', description: '' },
  action: { label: 'follow-up', plural: 'follow-ups', description: 'What rules trigger — parent message, escalation' },
  rule: { label: 'rule', plural: 'rules', description: '' },
  queue: { label: 'attendance queue', plural: 'attendance queues', description: '' },
};

/**
 * Parent attendance — orchestration definition. It has no user-facing input
 * surface, but the backwards-compatible attendanceRegisterService still binds
 * to it for legacy callers, so recording remains an explicit engine capability.
 * New class-display calls bind to the selected daily/period child instead.
 */
export const attendanceDomainConfig = assertDomainShape({
  name: 'attendance',
  label: 'Attendance',
  description: 'Daily and per-period presence tracking',
  protected: false,
  active: true,
  sortOrder: 3,
  icon: null,
  color: 'info',
  defaultInputMethod: 'list',
  defaultView: 'list',
  collections: {
    events: 'attendanceEvents',
    rules: 'attendanceRules',
    types: 'attendanceTypes',
    actions: 'attendanceActions',
    queue: 'attendanceQueue',
  },
  eventsCollection: 'attendanceEvents',
  rulesCollection: 'attendanceRules',
  // Register lives on the children, not here.
  capabilities: buildDomainCapabilities({
    collective: {
      enabled: true,
      scope: 'collection',
      aggregates: ['count', 'breakdown', 'trend', 'baseline'],
      audiences: ['staff', 'parent-summary'],
    },
    rules: { enabled: true, mechanisms: ['parent-loop'] },
    actions: { enabled: true, outcomes: ['parent-message-proposal'] },
    messages: { enabled: true, delivery: 'staff-reviewed-proposal' },
  }),
  surfaces: buildDomainSurfaces({
    quickIssue: false,
    queue: false,
    records: false,
    rules: false,
    dashboard: true,
    reports: true,
  }),
  primary: PRIMARY,
  followUp: FOLLOW_UP,
  // The register engine is built from THIS umbrella config (see
  // attendanceRegisterService), so the parent-loop trigger must be declared
  // here too — otherwise marking a register never proposes a parent message.
  // The condition carries its own statusFieldId, so it doesn't need the
  // register capability the children add.
  parentLoop: PARENT_LOOP,
  terminology: SHARED_TERMINOLOGY,
  contextTags: {},
  scope: { grades: [] },
});

export const dailyRegisterDomainConfig = assertDomainShape({
  name: 'daily-register',
  label: 'Daily Register',
  description: 'Official daily attendance record. One stamp per student per day. Source of truth for whether a student was at school.',
  parentDomainId: ATTENDANCE_DOMAIN_ID,
  protected: false,
  active: true,
  sortOrder: 4,
  icon: null,
  color: 'info',
  defaultInputMethod: 'list',
  defaultView: 'list',
  collections: {
    events: 'attendanceEvents',
    rules: 'attendanceRules',
    types: 'attendanceTypes',
    actions: 'attendanceActions',
    queue: 'attendanceQueue',
    registers: 'attendanceRegisters',
  },
  eventsCollection: 'attendanceEvents',
  rulesCollection: 'attendanceRules',
  registerCollection: 'attendanceRegisters',
  capabilities: buildDomainCapabilities({
    collective: {
      enabled: true,
      scope: 'domain',
      aggregates: ['count', 'breakdown', 'trend', 'baseline'],
      audiences: ['staff'],
    },
    register: {
      enabled: true,
      statusFieldId: 'status',
      implicitValue: 'present',
      collection: 'attendanceRegisters',
    },
    rules: { enabled: true, mechanisms: ['parent-loop'] },
    actions: { enabled: true, outcomes: ['parent-message-proposal'] },
    messages: { enabled: true, delivery: 'staff-reviewed-proposal' },
  }),
  surfaces: buildDomainSurfaces({ rules: false, dashboard: true, reports: true }),
  primary: PRIMARY,
  followUp: FOLLOW_UP,
  parentLoop: PARENT_LOOP,
  terminology: SHARED_TERMINOLOGY,
  contextTags: {},
  scope: { grades: [] },
});

export const periodRegisterDomainConfig = assertDomainShape({
  name: 'period-register',
  label: 'Period Register',
  description: 'Per-lesson attendance. Pre-marked from the daily register; teachers record deltas.',
  parentDomainId: ATTENDANCE_DOMAIN_ID,
  protected: false,
  active: true,
  sortOrder: 5,
  icon: null,
  color: 'info',
  defaultInputMethod: 'list',
  defaultView: 'list',
  // The only attendance domain that takes over the class display by default.
  // Opt-in is per-domain so schools' own register domains don't fight for the
  // same surface. Toggle lives on the domain edit form.
  inputSurfaces: ['class-display'],
  collections: {
    events: 'attendanceEvents',
    rules: 'attendanceRules',
    types: 'attendanceTypes',
    actions: 'attendanceActions',
    queue: 'attendanceQueue',
    registers: 'attendanceRegisters',
  },
  eventsCollection: 'attendanceEvents',
  rulesCollection: 'attendanceRules',
  registerCollection: 'attendanceRegisters',
  capabilities: buildDomainCapabilities({
    collective: {
      enabled: true,
      scope: 'domain',
      aggregates: ['count', 'breakdown', 'trend', 'baseline'],
      audiences: ['staff'],
    },
    register: {
      enabled: true,
      statusFieldId: 'status',
      implicitValue: 'present',
      collection: 'attendanceRegisters',
    },
    rules: { enabled: true, mechanisms: ['parent-loop'] },
    actions: { enabled: true, outcomes: ['parent-message-proposal'] },
    messages: { enabled: true, delivery: 'staff-reviewed-proposal' },
  }),
  surfaces: buildDomainSurfaces({
    rules: false,
    classDisplay: true,
    dashboard: true,
    reports: true,
  }),
  // Generic engine feature: pre-seed register cells from another domain's
  // events for the same student + date. The engine matches on field id
  // (`status` here) so any user-created domain can do the same trick.
  inheritsFrom: [
    { domainId: DAILY_REGISTER_DOMAIN_ID, fieldId: 'status' },
  ],
  primary: PRIMARY,
  followUp: FOLLOW_UP,
  parentLoop: PARENT_LOOP,
  terminology: SHARED_TERMINOLOGY,
  contextTags: {},
  scope: { grades: [] },
});

export default attendanceDomainConfig;
